import React, { useEffect, useState } from 'react';
import automotiveImg from '../../assets/images/automobile.webp';
import CourseIncludes from './CourseIncludes';
import CourseModules from './CourseModules';
import { useCart } from '../../context/CardContext';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const CourseDetailPageComponent = ({ course, modules }) => {
    const { addToCart, removeFromCart, cart } = useCart();
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(false);

    // Check screen size
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 992);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const isCourseInCart = cart.some(item => item.id === course.id);
    const student = localStorage.getItem('student');

    const handleAddToCart = () => {
        if (!student) {
            navigate('/login');
        } else {
            addToCart(course, 1);
            toast.success("Course added to cart");
        }
    };

    const handleBuyNow = () => {
        if (!student) {
            navigate('/login');
        } else {
            addToCart(course, 1);
            navigate("/cart");
        }
    };

    const handleRemoveFromCart = () => {
        removeFromCart(course.id);
        toast.success("Course removed from cart");
    };

    const discountAmount = course?.mrp - course?.price;
    const discountPercentage = course?.mrp && course?.price
        ? Math.round((discountAmount / course?.mrp) * 100)
        : 0;

    return (
        <div style={styles.container}>
            {/* Hero Section with gradient background */}
            <div style={styles.heroSection}>
                <div style={styles.contentWrapper}>
                    {/* Breadcrumb Navigation */}
                    <div style={styles.breadcrumb}>
                        <a href="#" style={styles.breadcrumbLink}>{course?.categoryDto?.name}</a>
                        <span style={styles.breadcrumbSeparator}>&gt;</span>
                        <a href="#" style={styles.breadcrumbLink}>{course?.subCategoryDto?.name}</a>
                        <span style={styles.breadcrumbSeparator}>&gt;</span>
                        <span style={styles.breadcrumbCurrent}>{course?.title}</span>
                    </div>

                    {/* Main Content - Desktop: Side by Side, Mobile: Stacked */}
                    <div className="course-main-content" style={styles.mainContent}>
                        {/* Course Header - Left Side */}
                        <div className="course-info-section" style={styles.courseHeader}>
                            <h1 style={styles.title}>{course?.title}</h1>

                            <div style={styles.metaInfo}>
                                <span style={styles.metaItem}>
                                    Created by "<a href="#" style={styles.creatorLink} target="_blank" rel="noopener noreferrer">  Amass Skill Ventures"</a>
                                </span>
                                <span style={styles.metaDivider}>•</span>
                                <span style={styles.metaItem}>{course?.categoryDto?.name}</span>
                                <span style={styles.metaDivider}>•</span>
                                <span style={styles.metaItem}>{course?.levelDto?.name}</span>
                            </div>

                            <div style={styles.languageInfo}>
                                <span style={styles.languageIcon}>🌐</span>
                                <span>{course?.language || 'Hindi & English'}</span>
                            </div>

                            {/* Course Description */}
                            <div style={styles.description}>
                                <h3 style={styles.sectionTitle}>Course Description</h3>
                                <div
                                    style={styles.descriptionContent}
                                    dangerouslySetInnerHTML={{ __html: course?.description }}
                                />
                            </div>
                        </div>

                        {/* Purchase Card - Right Side (Sticky on Desktop) */}
                        <div className="purchase-card-section" style={styles.purchaseSection}>
                            <div style={styles.purchaseCard}>
                                {/* Video/Image Preview */}
                                <div style={styles.mediaContainer}>
                                    {course?.introVideoUrl !== null ? (
                                        <video
                                            src={course?.introVideoUrl}
                                            controls
                                            style={styles.previewMedia}
                                            poster={course?.thumbnailUrl}
                                        />
                                    ) : (
                                        <img
                                            src={course?.thumbnailUrl}
                                            alt="Course Thumbnail"
                                            style={styles.previewMedia}
                                        />
                                    )}
                                </div>

                                <button style={styles.previewButton}>
                                    <span style={styles.previewIcon}>▶</span>
                                    Preview this course
                                </button>

                                {/* Price Information */}
                                <div style={styles.priceInfo}>
                                    <div style={styles.priceRow}>
                                        <span style={styles.currentPrice}>₹{course?.price}</span>
                                        {course?.mrp && (
                                            <>
                                                <span style={styles.originalPrice}>₹{course?.mrp}</span>
                                                <span style={styles.discount}>{discountPercentage}% off</span>
                                            </>
                                        )}
                                    </div>
                                    {course?.mrp && (
                                        <p style={styles.savings}>
                                            You save ₹{discountAmount}
                                        </p>
                                    )}
                                    <p style={styles.taxInfo}>(inclusive of all taxes)</p>
                                </div>

                                {/* Action Buttons */}
                                <div style={styles.actionButtons}>
                                    {!isCourseInCart ? (
                                        <button style={styles.cartButton} onClick={handleAddToCart}>
                                            Add to Cart
                                        </button>
                                    ) : (
                                        <button style={styles.removeButton} onClick={handleRemoveFromCart}>
                                            Remove from Cart
                                        </button>
                                    )}
                                    <button style={styles.buyButton} onClick={handleBuyNow}>
                                        Buy Now
                                    </button>
                                </div>

                                {/* Rating */}
                                <div style={styles.ratingSection}>
                                    <div style={styles.ratingValue}>
                                        <span style={styles.ratingNumber}>4.4</span>
                                        <div style={styles.stars}>★★★★★</div>
                                    </div>
                                    <a href="#reviews" style={styles.ratingsLink}>(1,478 ratings)</a>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Prerequisites and Benefits Section */}
                    <div className="additional-info" style={styles.additionalInfoSection}>
                        <div className="info-column" style={styles.infoColumn}>
                            <h2 style={styles.infoTitle}>Course Prerequisites</h2>
                            <ul style={styles.infoList}>
                                {course?.coursePrerequisits?.map((prerequisit, index) => (
                                    <li key={index} style={styles.infoListItem}>{prerequisit}</li>
                                ))}
                            </ul>
                        </div>

                        <div className="info-column" style={styles.infoColumn}>
                            <h2 style={styles.infoTitle}>Student Benefits</h2>
                            <ul style={styles.infoList}>
                                {course?.studentBenefits?.map((benefit, index) => (
                                    <li key={index} style={styles.infoListItem}>{benefit}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Course Includes Section */}
            <CourseIncludes course={course} modules={modules} />

            {/* Course Modules Section */}
            <CourseModules modules={modules} course={course} />
        </div>
    );
};


// CSS-in-JS Styles
const styles = {
    container: {
        fontFamily: 'Arial, sans-serif',
    },
    heroSection: {
        background: 'linear-gradient(135deg, #f0e1ce 0%, #fef5e7 100%)',
        paddingTop: '40px',
        paddingBottom: '60px',
    },
    contentWrapper: {
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '0 20px',
    },
    breadcrumb: {
        fontSize: '14px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '8px',
    },
    breadcrumbLink: {
        color: '#024076',
        textDecoration: 'none',
        transition: 'color 0.2s',
    },
    breadcrumbSeparator: {
        color: '#666',
        margin: '0 4px',
    },
    breadcrumbCurrent: {
        color: '#666',
    },
    mainContent: {
        display: 'flex',
        gap: '40px',
        alignItems: 'flex-start',
    },
    courseHeader: {
        flex: '1',
        minWidth: 0,
    },
    title: {
        fontSize: '36px',
        fontWeight: '700',
        color: '#1f2937',
        marginBottom: '16px',
        lineHeight: '1.3',
    },
    metaInfo: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        color: '#6b7280',
        marginBottom: '12px',
    },
    metaItem: {
        display: 'flex',
        alignItems: 'center',
    },
    metaDivider: {
        color: '#d1d5db',
    },
    creatorLink: {
        color: '#024076',
        textDecoration: 'none',
        fontWeight: '600',
    },
    languageInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        color: '#6b7280',
        marginBottom: '32px',
    },
    languageIcon: {
        fontSize: '16px',
    },
    description: {
        backgroundColor: '#ffffff',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    },
    sectionTitle: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#1f2937',
        marginBottom: '16px',
    },
    descriptionContent: {
        fontSize: '15px',
        lineHeight: '1.7',
        color: '#4b5563',
    },
    purchaseSection: {
        flex: '0 0 400px',
        position: 'sticky',
        top: '100px',
    },
    purchaseCard: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
        overflow: 'hidden',
    },
    mediaContainer: {
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%',
        backgroundColor: '#f3f4f6',
        overflow: 'hidden',
    },
    previewMedia: {
        position: 'absolute',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    previewButton: {
        width: '100%',
        backgroundColor: '#024076',
        color: '#ffffff',
        padding: '14px',
        border: 'none',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        transition: 'background-color 0.3s',
    },
    previewIcon: {
        fontSize: '12px',
    },
    priceInfo: {
        padding: '24px',
        borderBottom: '1px solid #e5e7eb',
    },
    priceRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '8px',
    },
    currentPrice: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#1f2937',
    },
    originalPrice: {
        fontSize: '18px',
        color: '#9ca3af',
        textDecoration: 'line-through',
    },
    discount: {
        backgroundColor: '#fef3c7',
        color: '#92400e',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: '600',
    },
    savings: {
        fontSize: '14px',
        color: '#10b981',
        fontWeight: '600',
        marginBottom: '4px',
    },
    taxInfo: {
        fontSize: '12px',
        color: '#9ca3af',
    },
    actionButtons: {
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        borderBottom: '1px solid #e5e7eb',
    },
    cartButton: {
        backgroundColor: '#ffffff',
        color: '#024076',
        padding: '14px',
        border: '2px solid #024076',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s',
    },
    removeButton: {
        backgroundColor: '#ef4444',
        color: '#ffffff',
        padding: '14px',
        border: 'none',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s',
    },
    buyButton: {
        backgroundColor: '#024076',
        color: '#ffffff',
        padding: '14px',
        border: 'none',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s',
    },
    ratingSection: {
        padding: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    ratingValue: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    ratingNumber: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#1f2937',
    },
    stars: {
        color: '#fbbf24',
        fontSize: '18px',
        letterSpacing: '2px',
    },
    ratingsLink: {
        color: '#024076',
        fontSize: '14px',
        textDecoration: 'none',
    },
    additionalInfoSection: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '40px',
        marginTop: '60px',
    },
    infoColumn: {
        backgroundColor: '#ffffff',
        padding: '32px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    },
    infoTitle: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#1f2937',
        marginBottom: '20px',
    },
    infoList: {
        infoList: {
            listStyle: 'disc',
            paddingLeft: '20px',
            margin: 0,
        },
    },
    infoListItem: {
        fontSize: '15px',
        color: '#4b5563',
        paddingLeft: '0',
        marginBottom: '12px',
        position: 'relative',
        lineHeight: '1.6',
    },
};

export default CourseDetailPageComponent;